// FMLD Department Registry — Foundational Table.
// Client-side registry (browser-persisted, same pattern as the Form Registry)
// since there is no backend endpoint for this yet. Seeded with FMLD's real
// department/unit structure; admin edits persist across reloads.
import { readRegistryCollection, writeRegistryCollection } from '../component/Admin/data/registryStore';

export const DEPARTMENT_REGISTRY_STORAGE_KEY = 'department-registry';

export const DEPARTMENT_CATEGORY_OPTIONS = [
  { value: 'department', label: 'Department' },
  { value: 'unit', label: 'Units' },
];

function slugify(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ─── Seed data — FMLD's real department/unit structure ─────────────────────
const DEPARTMENT_SEED = [
  'Ruminants & Monogastric',
  'Animal Health & Reproductive Services',
  'Ranch & Pastoral Services',
  'Pest Control Services',
  'Quality Assurance & Certification',
  'Veterinary Public Health & Epidemiology',
  'Livestock Extension & Business Development',
  'Reform Coordination',
  'Human Resource Management',
  'Finance & Accounts',
  'Planning, Research & Statistics',
  'Procurement',
  'General Services',
  'Office of The Permanent Secretary',
];

const UNIT_SEED = [
  // Office of The Permanent Secretary
  ['Special Duties', 'Office of The Permanent Secretary'],
  ['Legal', 'Office of The Permanent Secretary'],
  ['Internal Audit', 'Office of The Permanent Secretary'],
  ['PPP', 'Office of The Permanent Secretary'],
  ['Stock Verification', 'Office of The Permanent Secretary'],
  ['Zonal/State Office Coordination', 'Office of The Permanent Secretary'],
  ['Protocol', 'Office of The Permanent Secretary'],
  ['ACTU', 'Office of The Permanent Secretary'],
  ['Press & Public Relations', 'Office of The Permanent Secretary'],
  // Ruminants & Monogastric
  ['Dairy Production', 'Ruminants & Monogastric'],
  ['Beef Production', 'Ruminants & Monogastric'],
  ['Sheep and Goat Production', 'Ruminants & Monogastric'],
  ['Pig Production', 'Ruminants & Monogastric'],
  ['Poultry & Micro Livestock', 'Ruminants & Monogastric'],
  ['Livestock Animal Breeding & Conservation', 'Ruminants & Monogastric'],
  // Animal Health & Reproductive Services
  ['Ruminant Disease Prevention & Control', 'Animal Health & Reproductive Services'],
  ['Monogastric Animal Disease', 'Animal Health & Reproductive Services'],
  ['Veterinary Clinical Services & Sanitary Mandate', 'Animal Health & Reproductive Services'],
  ['Reproductive Health Mgt, Artificial Insemination Embryo Transfer', 'Animal Health & Reproductive Services'],
  ['Aquatic Health', 'Animal Health & Reproductive Services'],
  ['Bee Health', 'Animal Health & Reproductive Services'],
  // Ranch & Pastoral Services
  ['Grazing Reserves & Stock Route Development', 'Ranch & Pastoral Services'],
  ['Ranch Development', 'Ranch & Pastoral Services'],
  ['Feed & Fodder', 'Ranch & Pastoral Services'],
  ['Strategic Food Reserve', 'Ranch & Pastoral Services'],
  // Pest Control Services
  ['Transboundary Pest and Vector Control', 'Pest Control Services'],
  ['Integrated Pest Management', 'Pest Control Services'],
  ['Cross Border Collaboration', 'Pest Control Services'],
  ['Pesticides Quality Assurance and Lab Services', 'Pest Control Services'],
  // Quality Assurance & Certification
  ['Veterinary Certification', 'Quality Assurance & Certification'],
  ['Veterinary Legislation & Inspectorate', 'Quality Assurance & Certification'],
  ['Animal Welfare', 'Quality Assurance & Certification'],
  ['Veterinary Drug & Feed Safety', 'Quality Assurance & Certification'],
  ['Animal Identification & Traceability', 'Quality Assurance & Certification'],
  // Veterinary Public Health & Epidemiology
  ['Zoonotic Disease Control & Health & Antimicrobial Resistance', 'Veterinary Public Health & Epidemiology'],
  ['Biorisk Management & Emergency Preparedness & Response', 'Veterinary Public Health & Epidemiology'],
  ['Wildlife Disease Control & Animal Disease Risk Analysis', 'Veterinary Public Health & Epidemiology'],
  ['Abattoir Development & Food Safety Hygiene', 'Veterinary Public Health & Epidemiology'],
  ['Veterinary Epidemiology', 'Veterinary Public Health & Epidemiology'],
  // Finance & Accounts
  ['Fiscal & Final Reporting', 'Finance & Accounts'],
  ['Expenditure', 'Finance & Accounts'],
  ['Budget', 'Finance & Accounts'],
  // Livestock Extension & Business Development
  ['Livestock Extension Services', 'Livestock Extension & Business Development'],
  ['Business Development & Entrepreneurship', 'Livestock Extension & Business Development'],
  ['Livestock Cooperative Development', 'Livestock Extension & Business Development'],
  ['Gender & Youth Empowerment', 'Livestock Extension & Business Development'],
  // Reform Coordination
  ['SERVICOM', 'Reform Coordination'],
  ['Service Innovation', 'Reform Coordination'],
  // Human Resource Management
  ['Appointment, Promotion & Discipline', 'Human Resource Management'],
  ['Staff Welfare Training', 'Human Resource Management'],
  // Planning, Research & Statistics
  ['Planning', 'Planning, Research & Statistics'],
  ['ICT', 'Planning, Research & Statistics'],
  ['Monitoring & Evaluation', 'Planning, Research & Statistics'],
  ['Research and Statistics', 'Planning, Research & Statistics'],
  // Procurement
  ['Recurrent', 'Procurement'],
  ['Capital', 'Procurement'],
  // General Services
  ['Recurrent', 'General Services'],
];

function buildSeedRecords() {
  const departmentIdByName = new Map();
  const departments = DEPARTMENT_SEED.map((name) => {
    const id = `dept-${slugify(name)}`;
    departmentIdByName.set(name, id);
    return {
      id,
      name,
      category: 'department',
      parentDepartmentId: null,
    };
  });

  const usedUnitIds = new Set();
  const units = UNIT_SEED.map(([name, parentName]) => {
    const parentDepartmentId = departmentIdByName.get(parentName) ?? null;
    let id = `unit-${slugify(name)}-${slugify(parentName)}`;
    while (usedUnitIds.has(id)) id = `${id}-x`;
    usedUnitIds.add(id);
    return {
      id,
      name,
      category: 'unit',
      parentDepartmentId,
    };
  });

  return [...departments, ...units];
}

export const DEFAULT_DEPARTMENT_RECORDS = buildSeedRecords();

function normalizeDepartmentRecord(row, allRecordsById) {
  const category = row.category === 'unit' ? 'unit' : 'department';
  const parentDepartmentId = category === 'unit' ? (row.parentDepartmentId ?? null) : null;
  const parent = parentDepartmentId ? allRecordsById.get(parentDepartmentId) : null;
  return {
    id: row.id,
    uuid: row.id,
    name: row.name ?? '',
    category,
    categoryLabel: category === 'unit' ? 'Units' : 'Department',
    parentDepartmentId,
    departmentName: category === 'department' ? (row.name ?? '') : (parent?.name ?? row.departmentName ?? ''),
  };
}

// Raw read/write against the actual stored array only — deliberately not
// using getRegistryCollection()'s fallback-merge, since that merge can only
// ever add or override rows by id, never represent "this seeded row was
// deleted" (a subsequent read would just re-merge it back in from the seed).
function readRaw() {
  const cached = readRegistryCollection(DEPARTMENT_REGISTRY_STORAGE_KEY);
  if (cached) return cached;
  writeRegistryCollection(DEPARTMENT_REGISTRY_STORAGE_KEY, DEFAULT_DEPARTMENT_RECORDS);
  return DEFAULT_DEPARTMENT_RECORDS;
}

function readAll() {
  const raw = readRaw();
  const byId = new Map(raw.map((row) => [row.id, row]));
  return raw.map((row) => normalizeDepartmentRecord(row, byId));
}

function persist(rows) {
  writeRegistryCollection(DEPARTMENT_REGISTRY_STORAGE_KEY, rows);
}

export const departmentsApi = {
  async listAll() {
    return { items: readAll() };
  },

  async create(payload) {
    const raw = readRaw();
    const category = payload.category === 'unit' ? 'unit' : 'department';
    const id = `${category === 'unit' ? 'unit' : 'dept'}-${slugify(payload.name)}-${Date.now().toString(36)}`;
    const record = {
      id,
      name: String(payload.name ?? '').trim(),
      category,
      parentDepartmentId: category === 'unit' ? (payload.parentDepartmentId || null) : null,
    };
    const next = [...raw, record];
    persist(next);
    const byId = new Map(next.map((row) => [row.id, row]));
    return normalizeDepartmentRecord(record, byId);
  },

  async update(id, payload) {
    const raw = readRaw();
    const category = payload.category === 'unit' ? 'unit' : 'department';
    const next = raw.map((row) => (row.id === id ? {
      ...row,
      name: String(payload.name ?? row.name ?? '').trim(),
      category,
      parentDepartmentId: category === 'unit' ? (payload.parentDepartmentId || null) : null,
    } : row));
    persist(next);
    const byId = new Map(next.map((row) => [row.id, row]));
    const updated = next.find((row) => row.id === id);
    return normalizeDepartmentRecord(updated, byId);
  },

  async remove(id) {
    const raw = readRaw();
    // A department cannot be removed while units still reference it — mirrors
    // the registry-first rule elsewhere (no orphaned FK references).
    const hasDependentUnits = raw.some((row) => row.category === 'unit' && row.parentDepartmentId === id);
    if (hasDependentUnits) {
      throw new Error('This department still has units linked to it. Remove or reassign those units first.');
    }
    const next = raw.filter((row) => row.id !== id);
    persist(next);
    return { success: true };
  },
};

export function buildParentDepartmentOptions(rows = []) {
  return rows
    .filter((row) => row.category === 'department')
    .map((row) => ({ value: row.id, label: row.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
